/**
 * The document store — one tree, one pristine baseline, and a patch stack behind a named vocabulary.
 *
 * Document-model ADR §5b. **No component reads or writes the draft tree directly.** What leaves this
 * module is a set of narrow hooks and a set of named operations; the tree's shape is never the
 * interface. Zustand's idiom invites the opposite — `useStore((s) => s.draft.phases[i].days[j])`,
 * mutated in place — and that idiom is banned here, because it makes every caller learn the whole
 * document and turns every invariant into a convention.
 *
 * ── Why patches rather than snapshots ────────────────────────────────────────────────────────
 * ADR §5. The tree runs to 12 phases × 14 days × 20 exercises × 20 sets before materialisation, and
 * an editing session is long and made of many small edits — retype a weight, rename a session, drag
 * a day. Snapshotting the whole tree on every keystroke is wasted memory and a wasted diff on every
 * undo; a patch is proportional to the edit. That is also why every operation MUTATES its Immer
 * draft rather than returning a new tree: a producer that returned one would record a single
 * root-level `replace` patch, which is a snapshot with extra steps.
 *
 * ── `enablePatches()` is called HERE, once ───────────────────────────────────────────────────
 * It is a global Immer switch, and `produceWithPatches` THROWS without it rather than degrading —
 * so a build that lost this call would fail on the first edit, loudly. It lives in this module
 * because this module is the only patch consumer in the app, and a plugin enabled next to its user
 * cannot be tree-shaken away from it. `setAutoFreeze` is deliberately NOT touched: freezing is
 * Immer's default and it is what turns "a component mutated the tree behind the store's back" from a
 * silent corruption into a thrown error.
 *
 * ── The one honest limitation of O(1) dirtiness ──────────────────────────────────────────────
 * `dirty` is the emptiness of the undo stack (ADR §5), not a comparison against the baseline — the
 * store already knows in O(1) whether anything was pushed, and re-fingerprinting a tree this size on
 * every render is a cost the ADR explicitly declines. Two consequences follow, and both are
 * intentional rather than defects to guard against:
 *
 * · Undoing back to the loaded state clears the stack, so the plan reads as clean. It IS clean — the
 *   tree is byte-identical to what was loaded.
 * · The stack is capped at 200 entries. A trainer who makes more than that and then undoes all the
 *   way down reaches a tree that still differs from the baseline while the stack is empty, so it
 *   reads as clean when it is not. Reaching it takes 200 consecutive undos past a 200-edit session;
 *   the alternative is an unbounded stack, which trades a rare wrong dot for a real memory leak.
 *
 * ── One store, one document ──────────────────────────────────────────────────────────────────
 * A module-level singleton, because the builder is one route holding one plan (execution ADR §4) and
 * two documents in memory is the state the whole view architecture exists to avoid. `loadPlanDraft`
 * replaces everything; navigating between plans is a load.
 *
 * This file is `lib/` rather than `model/` because it holds STATE and a React binding. Every decision
 * it appears to make lives in `model/draftOperations.ts`, which is where the tests are.
 */

import { applyPatches, enablePatches, produceWithPatches, type Draft, type Patch } from 'immer';
import { create } from 'zustand';

import type { MeasurementFieldKey } from '~/shared/measurement';

import {
  applyProposalChanges,
  isAiWrittenValue,
  markAiWritten,
  NO_AI_APPLY_RECORDS,
  NO_AI_APPLY_STANDING,
  NO_AI_WRITTEN_MARKS,
  proposalKey,
  withApplyStanding,
  type AiApplyRecord,
  type AiApplyRecords,
  type AiApplyStanding,
  type AiProposal,
  type AiWrittenMarks,
  type AiWrittenValueKey,
} from '../model/aiProposal';
import * as operations from '../model/draftOperations';
import type { ExerciseAuthoring } from '../model/draftOperations';
import { NO_PROGRESSION_RULES, type ProgressionRules } from '../model/progressionRules';
import { NO_STORED_RULES } from '../model/progressionWire';
import { findDayPhaseWindow, findExerciseCycleWindow } from '../model/cycleWindow';
import type { DayCycleScope, DayCycleWindow } from '../model/dayCycleWindow';
import { canLiftDay, canLiftExercise } from '../model/dropGates';
import { consumedCycleCount, isLivePlan } from '../model/livePlanLocks';
import { applyMove, moveOutcome, type MoveCommand, type MoveOutcome } from '../model/moveCommands';
import { firstEmptyPhaseName } from '../model/draftToRequest';
import type { AuthorableExerciseRole, ExercisePick } from '../model/draftFactory';
import type {
  LibraryBatchInsert,
  MatrixCellAddress,
  SetAddress,
  SetFieldPatch,
  SupersetJoin,
  WarmupInsert,
} from '../model/draftOperations';
import type { PlanCategoryKey } from '../model/planCategory';
import type {
  DayDraft,
  ExerciseDraft,
  PhaseType,
  PlanDraft,
  PlanSchedulingMode,
} from '../model/planDraft.types';

// Once, at module load. See the header: `produceWithPatches` throws without it.
enablePatches();

/**
 * How many edits the undo stack holds. Past this, the OLDEST is dropped — a trainer is far more
 * likely to want the last thirty edits back than the first, and an unbounded stack on a document
 * this size is a session-long memory leak.
 */
const UNDO_LIMIT = 200;

/**
 * One recorded edit: the patches that made it, the patches that unmake it, and, for an apply of
 * Buddy's proposal, which changes it applied — so an undo and a redo move them with the tree.
 */
type UndoEntry = readonly [patches: Patch[], inversePatches: Patch[], apply?: AiApplyRecord];

interface PlanDraftState {
  /** The tree being edited. `null` before the first load and after `clearPlanDraft`. */
  readonly draft: PlanDraft | null;
  /**
   * The tree exactly as it was LOADED, never touched by an edit. The 409 flow diffs the winning tree
   * against THIS, not against `draft` — see `summariseConflict`, which explains why diffing against
   * the edited draft answers the wrong question.
   */
  readonly baseline: PlanDraft | null;
  readonly past: readonly UndoEntry[];
  readonly future: readonly UndoEntry[];
  /**
   * How many times the tree has CHANGED since this store was created. Monotonic, never reset by a
   * load — it is a change signal, not a measure of anything.
   *
   * It exists because nothing else here can answer "did the document just change?". `draft` can, but
   * subscribing to it is the wide selector this module exists to prevent. `past.length` looks like it
   * can and cannot: it saturates at `UNDO_LIMIT`, so the 201st consecutive edit leaves it unchanged,
   * and it decrements on undo, so two edits either side of an undo can produce the same number.
   *
   * Its one consumer is the builder's save banner, which has to stop claiming a save failed the
   * moment the trainer starts editing again. A subscriber that missed one edit would leave a stale
   * refusal on screen over a document that no longer matches it.
   */
  readonly revision: number;
  /**
   * The client an UNSAVED plan is being built for, or `null`.
   *
   * Set only by `seedPlanDraft`, from the create dialog's client entry, and bound to that seed: a
   * `loadPlanDraft` of any other tree clears it, so a client can never be carried onto a plan that
   * was not started for them. While it stands the builder offers no Zapisz and its Przypisz sends
   * the tree itself (`features/assign-plan`'s unsaved entry). It outlives the assignment — it is
   * who has the plan then — and goes when `releasePlanDraft` drops the assigned tree.
   */
  readonly recipient: PlanRecipient | null;
  /**
   * Whether the unsaved plan in the store has been ASSIGNED — sent whole to the client it was built
   * for. It still has no id (the client's fork is a different row), but nothing about it is unsaved
   * any more: `settleAssignedPlanDraft` sets this, the leave guard stops asking, and
   * `releasePlanDraft` drops the tree instead of keeping it.
   */
  readonly assigned: boolean;
  /**
   * The items Buddy wrote into the tree, each with the fingerprint it had then (ai-plan-builder R15).
   * Outside the patch stack on purpose: a mark is compared against the item as it stands, so an
   * edit, an undo or a redo needs no bookkeeping here. See `isAiWritten`.
   */
  readonly aiWritten: AiWrittenMarks;
  /**
   * Which of each proposal's changes an apply in this session made and an undo has not taken back
   * (ai-plan-builder R19). Moved by the undo entry an apply records, never by an edit: the trainer
   * editing what Buddy wrote does not take an apply back. Kept across a load, as the marks are: a
   * save reloads the tree, and what it saved is still applied. See `appliedChanges`.
   */
  readonly aiApplied: AiApplyRecords;
}

/** Who an unsaved plan is being built for — the client detail's „Przypisz plan". */
export interface PlanRecipient {
  readonly clientId: number;
  /** The client's full name, nominative, as the roster reads it. Never declined. */
  readonly clientName: string;
}

const EMPTY: PlanDraftState = {
  draft: null,
  baseline: null,
  past: [],
  future: [],
  revision: 0,
  recipient: null,
  assigned: false,
  aiWritten: NO_AI_WRITTEN_MARKS,
  aiApplied: NO_AI_APPLY_RECORDS,
};

const usePlanDraftStore = create<PlanDraftState>()(() => EMPTY);

/**
 * Run one operation against the tree and record it.
 *
 * Three properties are worth naming because callers depend on all three:
 *
 * · **A no-op edit records nothing.** Immer returns an empty patch list when the recipe changed
 *   nothing, and pushing that would make the plan read as dirty because the trainer retyped a name
 *   as itself — and would put an undo on the stack that visibly does nothing when pressed.
 * · **A new edit clears the redo stack.** Standard, and structurally necessary here: the future
 *   entries were recorded against a state this edit has now replaced, and Immer patches address
 *   arrays by index.
 * · **Returning the same state object notifies nobody.** Zustand compares with `Object.is` before
 *   merging (`node_modules/zustand/esm/vanilla.mjs`), so the guard paths below cost no render.
 */
function runOperation(recipe: (draft: Draft<PlanDraft>) => void, apply?: () => AiApplyRecord): void {
  usePlanDraftStore.setState((state) => {
    if (state.draft === null) return state;
    const [next, patches, inversePatches] = produceWithPatches(state.draft, recipe);
    if (patches.length === 0) return state;
    const record = apply?.();
    const entry: UndoEntry = record === undefined ? [patches, inversePatches] : [patches, inversePatches, record];
    return {
      draft: next,
      past: [...state.past, entry].slice(-UNDO_LIMIT),
      future: [],
      revision: state.revision + 1,
      ...(record === undefined ? {} : { aiApplied: withApplyStanding(state.aiApplied, record, true) }),
    };
  });
}

/**
 * Load a tree — the first read, a successful save's fresh response, and adopting a winner after a
 * lost race are all THIS operation.
 *
 * They collapse into one because they are one act: replace the tree, replace the baseline, discard
 * the history. ADR §4's sequence diagram says exactly that for a save ("Replace baseline, clear
 * patch stack") and for an adoption ("Replace baseline + draft with winning tree, clear patch
 * stack"), and a separate `markSaved` would be a second name for the same three assignments — the
 * kind of pass-through that only ever drifts.
 *
 * The history is discarded rather than kept, and that is the honest behaviour: an inverse patch
 * recorded against the pre-save tree addresses arrays by index, and the server may have renumbered,
 * merged or dropped rows while materialising the save. Offering an undo that could silently write to
 * a different row than the one the trainer edited is worse than offering none.
 */
export function loadPlanDraft(draft: PlanDraft): void {
  // A load is a different document, and a client belongs to the seed they were chosen for.
  resetTo(draft, null);
}

/**
 * Seed a plan that has never been stored — the create dialog's one act — optionally FOR a client.
 *
 * A load in every respect (the tree, the baseline, no history), plus the one fact a load must never
 * carry: who the plan is being built for. `recipient` is `null` from the plan library, where the
 * plan becomes a library entry on its first Zapisz.
 */
export function seedPlanDraft(draft: PlanDraft, recipient: PlanRecipient | null): void {
  resetTo(draft, recipient);
}

/** Replace the tree and the baseline, discard the history, and bind the document to `recipient`. */
function resetTo(draft: PlanDraft, recipient: PlanRecipient | null): void {
  // `revision` is advanced rather than reset: it means "the tree changed", and a load changes it
  // more than any edit does. Resetting it to zero could hand a subscriber the same number twice
  // across a load and read as "nothing happened".
  usePlanDraftStore.setState((state) => ({
    draft,
    baseline: draft,
    past: [],
    future: [],
    revision: state.revision + 1,
    recipient,
    assigned: false,
  }));
}

/**
 * The unsaved plan was assigned to its client: nothing about it is unsaved any more.
 *
 * The tree stays — the builder is still on screen under the confirmation — but it becomes its own
 * baseline with no history, and `assigned` makes `releasePlanDraft` drop it and the leave guard stop
 * asking. The recipient STAYS: it is who has the plan now, the builder behind the confirmation still
 * offers no Zapisz for it, and closing the confirmation returns the trainer to that client. It goes
 * with the tree, when the release drops both. A no-op on a stored plan or an empty store, which have
 * nothing to settle.
 */
export function settleAssignedPlanDraft(): void {
  usePlanDraftStore.setState((state) =>
    state.draft === null || state.draft.id !== null
      ? state
      : {
          baseline: state.draft,
          past: [],
          future: [],
          revision: state.revision + 1,
          assigned: true,
        },
  );
}

/** Drop the document unconditionally. The blunt instrument; see `releasePlanDraft` for the route's. */
export function clearPlanDraft(): void {
  usePlanDraftStore.setState(EMPTY);
}

/**
 * Drop the document **unless it is an unsaved new plan.**
 *
 * What the builder route calls on unmount. A stored plan's tree is a CACHE of something the server
 * still has — leaving it behind would render the previous plan for a frame under the next plan's
 * heading, and a save fired before the next query settled would send it. So it goes.
 *
 * A plan the create modal seeded exists NOWHERE else. Clearing it is destroying the trainer's work,
 * and there are two ways to reach that here, one of them silent:
 *
 * · **`StrictMode` double-invokes effects in development**, so the route's cleanup runs immediately
 *   after its first mount. For a stored plan that is harmless — the query's load effect refills the
 *   store on the second mount. For a seeded draft there is nothing to refill from, and
 *   `/plans/nowy` reported the draft gone the instant it opened. Found by creating a plan and
 *   watching it vanish; it would have shipped as "the create button does nothing".
 * · Leaving `/plans/nowy` for the library and coming back. The blocker asks first, and if the
 *   trainer says stay, the draft must still be there.
 *
 * The tree that survives is bounded and harmless: it is one document, `usePlanDocument` ignores it
 * for any STORED plan (it matches on id), and the next load replaces it outright.
 */
export function releasePlanDraft(): void {
  usePlanDraftStore.setState((state) =>
    state.draft !== null && state.draft.id === null && !state.assigned ? state : EMPTY,
  );
}

/**
 * Drop the document **only if it is an unsaved new plan** — `releasePlanDraft`'s mirror image.
 *
 * The trainer's own "Zacznij od nowa", and a sign-out. A stored plan's tree is left alone: it is a
 * cache of the server and the builder showing it owns its lifetime.
 */
export function discardUnsavedPlanDraft(): void {
  usePlanDraftStore.setState((state) =>
    state.draft !== null && state.draft.id === null ? EMPTY : state,
  );
}

/**
 * Call `listener` whenever the TREE changes — an edit, an undo, a load, a clear. Not a React
 * binding, so it is not exported past the slice: its one subscriber is the crash-recovery writer in
 * `lib/unsavedPlanRecord.ts`, which lives for the page's lifetime rather than a component's.
 *
 * Keyed on the tree's identity rather than on `revision`, because `clearPlanDraft` resets the
 * revision to zero and a subscriber comparing numbers could miss that change.
 */
export function subscribeToPlanDraftChanges(listener: () => void): () => void {
  return usePlanDraftStore.subscribe((state, previous) => {
    if (state.draft !== previous.draft) listener();
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The vocabulary. One exported function per act a trainer can perform, each forwarding to the
// operation that owns its invariants.
//
// These look like pass-throughs and are not: what they add is the PRODUCER — the patch recording,
// the redo-stack clear, the no-op guard and the revision bump — and doing that once per act is the
// whole reason a component never touches `produceWithPatches`. Every invariant lives one layer
// down, in `model/draftOperations.ts`, which is where the tests are and where a rule can be read
// without a store.
//
// The `import * as operations` is deliberate: a named import per function would be a
// twenty-line block that has to be edited twice for every new act, and the namespace makes each
// forward read as `operations.x` — visibly the model's, not this file's.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** Rename the workout a day holds. */
export function renameSession(daySlotToken: string, name: string): void {
  runOperation((draft) => {
    operations.renameSession(draft, daySlotToken, name);
  });
}

/** Write prescription values on one set. */
export function setSetField(address: SetAddress, patch: SetFieldPatch): void {
  runOperation((draft) => {
    operations.setSetField(draft, address, patch);
  });
}

/** Change one set's type — the badge, and the menu behind it. */
export function changeSetType(address: SetAddress, nextType: string): void {
  runOperation((draft) => {
    operations.changeSetType(draft, address, nextType);
  });
}

/** Give the exercise a back-off descent, resolving the top it hangs off first. */
export function makeBackoffSet(address: SetAddress, topSetIndex: number | null): void {
  runOperation((draft) => {
    operations.makeBackoffSet(draft, address, topSetIndex);
  });
}

/** Insert a set below `afterSetIndex`, copying that row's prescription. */
export function insertSetBelow(exerciseSlotToken: string, afterSetIndex: number): void {
  runOperation((draft) => {
    operations.insertSetBelow(draft, exerciseSlotToken, afterSetIndex);
  });
}

/** Remove one set. An exercise always keeps at least one. */
export function removeSet(address: SetAddress): void {
  runOperation((draft) => {
    operations.removeSet(draft, address);
  });
}

/**
 * Add an exercise to a day from a library pick, and answer with its slot token.
 *
 * `null` when nothing was added — no such day, or the day is at its cap. The recipe runs
 * synchronously inside `setState`, so the token is available by the time this returns; a caller that
 * does not need it ignores the value, exactly as every caller did before 7 September 2026.
 */
export function addExercise(
  daySlotToken: string,
  pick: ExercisePick,
  role: AuthorableExerciseRole = 'Main',
): string | null {
  let created: string | null = null;
  runOperation((draft) => {
    created = operations.addExercise(draft, daySlotToken, pick, role);
  });
  return created;
}

/**
 * Add SEVERAL library picks to a day in one act — the library's batch add.
 *
 * ONE `runOperation`, so the whole selection is one patch and **one undo entry**; see
 * `operations.addExercises` for why the gate is asked once for the batch and why an overrun refuses
 * the lot. The answer is the operation's own, because the caller has to word a refusal and has to
 * say how many movements arrived.
 */
export function addExercisesFromLibrary(
  daySlotToken: string,
  picks: readonly ExercisePick[],
  role: AuthorableExerciseRole = 'Main',
): LibraryBatchInsert {
  let outcome: LibraryBatchInsert = { kind: 'refused', reason: 'unknownTarget' };
  runOperation((draft) => {
    outcome = operations.addExercises(draft, daySlotToken, picks, role);
  });
  return outcome;
}

/**
 * Give one exercise its own warm-up — the mini-group. Answers what it did, because the caller has to
 * word a refusal and has to name the card it just made.
 *
 * `runOperation` applies the recipe synchronously inside `setState`, so the outcome is available by
 * the time this returns — the same shape, and the same reason, as `addExercise` and `groupAsSuperset`
 * above it.
 */
export function insertWarmupFor(
  targetExerciseSlotToken: string,
  pick: ExercisePick,
): WarmupInsert {
  let outcome: WarmupInsert = { kind: 'refused', reason: 'unknownTarget' };
  runOperation((draft) => {
    outcome = operations.insertWarmupFor(draft, targetExerciseSlotToken, pick);
  });
  return outcome;
}

/** Remove an exercise. Guarded — see `exerciseRemovalRefusal`. */
export function removeExercise(exerciseSlotToken: string): void {
  runOperation((draft) => {
    operations.removeExercise(draft, exerciseSlotToken);
  });
}

/** Move an exercise into or out of the day's warm-up block. */
export function setExerciseRole(exerciseSlotToken: string, role: AuthorableExerciseRole): void {
  runOperation((draft) => {
    operations.setExerciseRole(draft, exerciseSlotToken, role);
  });
}

/** The coaching tip on one exercise. Real, server-stored, shown to the athlete. */
export function setExerciseNotes(exerciseSlotToken: string, notes: string): void {
  runOperation((draft) => {
    operations.setExerciseNotes(draft, exerciseSlotToken, notes);
  });
}

/** Switch an exercise between absolute kilograms and %-of-1RM. */
export function setPrescriptionMode(exerciseSlotToken: string, mode: string): void {
  runOperation((draft) => {
    operations.setPrescriptionMode(draft, exerciseSlotToken, mode);
  });
}

/** Bracket an exercise with the one below it. */
export function groupWithNext(exerciseSlotToken: string): void {
  runOperation((draft) => {
    operations.groupWithNext(draft, exerciseSlotToken);
  });
}

/**
 * Bracket any two or more selected exercises of one day, pulling them together, and answer with
 * what was joined.
 *
 * The day is named as well as the members because a superset is a decision inside ONE day, and a
 * selection collected by the panel can only ever mean that day's rows.
 *
 * `null` when nothing was joined — a locked phase, or fewer than two eligible members. The caller
 * announces on this answer and not on the press; see the operation's own header, and `addExercise`
 * above for the same shape and the same reason.
 */
export function groupAsSuperset(
  daySlotToken: string,
  exerciseSlotTokens: readonly string[],
): SupersetJoin | null {
  let joined: SupersetJoin | null = null;
  runOperation((draft) => {
    joined = operations.groupAsSuperset(draft, daySlotToken, exerciseSlotTokens);
  });
  return joined;
}

/** Pull an exercise out of its bracket. */
export function unlinkSuperset(exerciseSlotToken: string): void {
  runOperation((draft) => {
    operations.unlinkSuperset(draft, exerciseSlotToken);
  });
}

/**
 * Create a training day on an empty weekday, and answer with its slot token so the caller can open
 * the dock on the day it just made.
 *
 * The token is read back from the tree rather than returned by the operation, because an operation
 * mutates an Immer draft and returns nothing — a mutator that also returned a value would be two
 * shapes in one, and the draft it minted into is discarded by the time the producer resolves.
 */
export function addDay(phaseIndex: number, dayOfWeek: number | null): string | null {
  runOperation((draft) => {
    operations.addDay(draft, phaseIndex, dayOfWeek);
  });
  const draft = usePlanDraftStore.getState().draft;
  return draft === null ? null : operations.addedDayToken(draft, phaseIndex, dayOfWeek);
}

/**
 * Move the gap after a day — the tape's rest stepper. A plain field patch; see the operation.
 */
export function setRestDaysAfter(daySlotToken: string, days: number): void {
  runOperation((draft) => {
    operations.setRestDaysAfter(draft, daySlotToken, days);
  });
}

/** Delete a training day and everything in it. See `removeDay`. */
export function removeDay(daySlotToken: string): void {
  runOperation((draft) => {
    operations.removeDay(draft, daySlotToken);
  });
}

/**
 * Apply the changes of Buddy's proposal the trainer accepted, with the changes they depend on —
 * the apply button's one act (ai-plan-builder R17). ONE `runOperation`, so everything it writes is one
 * patch and **one undo entry** (R19).
 *
 * Changes the trainer's edits have overtaken are not applied whatever was accepted (R18), and neither
 * is a change bringing in an exercise whose library row the proposal did not carry; see
 * `applyProposalChanges`. The items written are marked as Buddy's until the trainer edits them (R15).
 * Answers the ids of the changes that were made.
 */
export function applyProposal(proposal: AiProposal, accepted: readonly string[]): ReadonlySet<string> {
  let applied: ReadonlySet<string> = new Set();
  const before = usePlanDraftStore.getState().draft;
  runOperation(
    (draft) => {
      applied = applyProposalChanges(draft, proposal, accepted);
    },
    () => ({ proposalKey: proposalKey(proposal), changeIds: [...applied] }),
  );
  const draft = usePlanDraftStore.getState().draft;
  if (before !== null && draft !== null && applied.size > 0) {
    usePlanDraftStore.setState((state) => ({ aiWritten: markAiWritten(state.aiWritten, before, draft, proposal, applied) }));
  }
  return applied;
}

// `excludeDayCycle` and `retireDayFrom` deliberately have NO store wrapper. The one surface that
// removes a training from a week is the calendar's card menu, and it edits a LOCAL tree rather than
// this singleton — the builder may be holding unsaved work in it. A wrapper with no caller is a
// vocabulary entry nobody keeps correct.

/** Write a day's whole authored lifespan at once — the ADD and RESTORE flows' one write. */
export function setDayCycleWindow(daySlotToken: string, window: DayCycleWindow): void {
  runOperation((draft) => {
    operations.setDayCycleWindow(draft, daySlotToken, window);
  });
}

/** Put a day back into one cycle, with the content and the progression chain it already had. */
export function restoreDayCycle(
  daySlotToken: string,
  cycle: number,
  scope: DayCycleScope,
): void {
  runOperation((draft) => {
    operations.restoreDayCycle(draft, daySlotToken, cycle, scope);
  });
}

/**
 * Add a SECOND training on a weekday that already carries one, running in this cycle alone, and
 * answer with its slot token so the caller can open the dock on the day it just made.
 *
 * The token is read back from the tree for the reason `addDay` reads its own back, and through a
 * different lookup: the weekday now holds two rows and the new one is not the first.
 */
export function addDayOneTime(
  phaseIndex: number,
  dayOfWeek: number,
  cycle: number,
): string | null {
  runOperation((draft) => {
    operations.addDayOneTime(draft, phaseIndex, dayOfWeek, cycle);
  });
  const draft = usePlanDraftStore.getState().draft;
  return draft === null
    ? null
    : operations.addedOneTimeDayToken(draft, phaseIndex, dayOfWeek, cycle);
}

/** Copy a training day, minting a fresh slot for it and for every exercise under it. */
export function duplicateDay(daySlotToken: string): void {
  runOperation((draft) => {
    operations.duplicateDay(draft, daySlotToken);
  });
}

/**
 * Append a phase, and answer with the index it landed at so the caller can select its tab.
 *
 * `null` when the add was refused — the phase cap, or the plan-wide cycle ceiling. Read back from
 * the tree for the same reason `addDay` reads its token back: a mutator returns nothing, and the
 * draft it pushed into is discarded by the time the producer resolves.
 */
export function addPhase(type: PhaseType, name: string): number | null {
  const before = usePlanDraftStore.getState().draft?.phases.length ?? 0;
  runOperation((draft) => {
    operations.addPhase(draft, type, name);
  });
  const after = usePlanDraftStore.getState().draft?.phases.length ?? 0;
  return after > before ? after - 1 : null;
}

/**
 * Copy a phase and everything in it, and answer with the index the copy landed at.
 *
 * The copy sits directly after its source, so the index is `sourceIndex + 1` — but only when the
 * operation actually ran, which is what the length comparison establishes. `null` on a refusal.
 */
export function duplicatePhase(phaseIndex: number, name: string): number | null {
  const before = usePlanDraftStore.getState().draft?.phases.length ?? 0;
  runOperation((draft) => {
    operations.duplicatePhase(draft, phaseIndex, name);
  });
  const after = usePlanDraftStore.getState().draft?.phases.length ?? 0;
  return after > before ? phaseIndex + 1 : null;
}

/** Delete a phase. Guarded — a plan keeps one, and a consumed phase keeps all of itself. */
export function removePhase(phaseIndex: number): void {
  runOperation((draft) => {
    operations.removePhase(draft, phaseIndex);
  });
}

/** Rename a phase. Free on a locked phase — a name covers no cycle. */
export function setPhaseName(phaseIndex: number, name: string): void {
  runOperation((draft) => {
    operations.setPhaseName(draft, phaseIndex, name);
  });
}

/** Switch a phase between Build and Deload. Refused inside the consumed prefix. */
export function setPhaseType(phaseIndex: number, type: PhaseType): void {
  runOperation((draft) => {
    operations.setPhaseType(draft, phaseIndex, type);
  });
}

/** Set a phase's length in cycles, clamped into what the live plan and the cap allow. */
export function setPhaseCycles(phaseIndex: number, cycles: number): void {
  runOperation((draft) => {
    operations.setPhaseCycles(draft, phaseIndex, cycles);
  });
}

/** Rename the plan. A blank name is refused — the wire has no fallback for one. */
export function setPlanName(name: string): void {
  runOperation((draft) => {
    operations.setPlanName(draft, name);
  });
}

/**
 * Set the plan's category. It writes the category ALONE since 15 August 2026 — the cover it used to
 * drag with it is a server-pool row this app cannot choose, and the display fallback follows the
 * category on its own. See the operation.
 */
export function setPlanCategory(category: PlanCategoryKey): void {
  runOperation((draft) => {
    operations.setPlanCategory(draft, category);
  });
}

/** The trainer's note on the session — shown to the client when they start the workout. */
export function setSessionNotes(daySlotToken: string, notes: string): void {
  runOperation((draft) => {
    operations.setSessionNotes(draft, daySlotToken, notes);
  });
}

/** Move the day's default rest, carrying along every exercise that was following it. */
export function setDayRest(daySlotToken: string, seconds: number): void {
  runOperation((draft) => {
    operations.setDayRest(draft, daySlotToken, seconds);
  });
}

/** Show or hide one exercise's rest column. Hiding restamps its sets back to the day's value. */
export function toggleRestColumn(exerciseSlotToken: string): void {
  runOperation((draft) => {
    operations.toggleRestColumn(draft, exerciseSlotToken);
  });
}

/** Open a measurement column the exercise measures but is not prescribing yet. */
export function revealColumn(exerciseSlotToken: string, field: MeasurementFieldKey): void {
  runOperation((draft) => {
    operations.revealColumn(draft, exerciseSlotToken, field);
  });
}

/**
 * Close a measurement column by clearing what is behind it — every set and every per-cycle pin.
 *
 * Destructive, and the caller is where that is worded: nothing below this asks. What survives the
 * clearing is `removeColumnValues`' decision, not this function's.
 */
export function hideColumn(exerciseSlotToken: string, field: MeasurementFieldKey): void {
  runOperation((draft) => {
    operations.hideColumn(draft, exerciseSlotToken, field);
  });
}

/**
 * Commit one exercise's progression authoring — the rule sheet's „Zastosuj do draftu", and the ONE
 * write that changes what the matrix projects.
 *
 * It takes EVERY column the session edited, not only the one on screen, and the whole list lands
 * inside one `runOperation`: one decision, one undo entry.
 */
export function applyExerciseAuthoring(
  exerciseSlotToken: string,
  authorings: readonly ExerciseAuthoring[],
): void {
  runOperation((draft) => {
    operations.applyExerciseAuthoring(draft, exerciseSlotToken, authorings);
  });
}

/** Pin one matrix cell to a value, or clear it back to the rule when the value is `null`. */
export function setMatrixOverride(
  exerciseSlotToken: string,
  address: MatrixCellAddress,
  valueRaw: number | null,
): void {
  runOperation((draft) => {
    operations.setMatrixOverride(draft, exerciseSlotToken, address, valueRaw);
  });
}

/**
 * Write the base („BAZA") value a matrix row projects from — editing the C1 column. NOT a pin: it
 * moves the row's own prescription through the set table's writer, and every later cycle follows.
 * See the operation's note for the owner's call of 15 September 2026.
 */
export function setMatrixBaseValue(
  exerciseSlotToken: string,
  address: MatrixCellAddress,
  valueRaw: number | null,
): void {
  runOperation((draft) => {
    operations.setMatrixBaseValue(draft, exerciseSlotToken, address, valueRaw);
  });
}

/**
 * Hand EVERY pinned cell on one exercise back to its rule — the sheet's „Wyczyść nadpisania". One
 * press, one draft operation, one undo entry; see the operation's own note.
 */
export function clearExercisePins(exerciseSlotToken: string): void {
  runOperation((draft) => {
    operations.clearExercisePins(draft, exerciseSlotToken);
  });
}

/** Hand one pinned cell back to the rule — the revert × on an overridden cell. */
export function revertMatrixOverride(
  exerciseSlotToken: string,
  address: Pick<MatrixCellAddress, 'setIndex' | 'cycleIndex'>,
): void {
  runOperation((draft) => {
    operations.revertMatrixOverride(draft, exerciseSlotToken, address);
  });
}

/**
 * Convert a pin at one cycle into a base shift, so the curve passes through the pinned value and
 * carries on from it — the calendar day editor's „ten tydzień i wszystkie kolejne".
 */
export function rebaseOverrideForward(
  exerciseSlotToken: string,
  address: MatrixCellAddress,
): void {
  runOperation((draft) => {
    operations.rebaseOverrideForward(draft, exerciseSlotToken, address);
  });
}

/**
 * Ask what a drag would do WITHOUT doing it — the question a drop target asks on every pointer move
 * to decide whether it draws an indicator at all.
 *
 * Outside React because it runs inside Pragmatic's `canDrop`, which is a plain callback on a DOM
 * element and has no render to read a hook from. It reads the CURRENT tree for the same reason
 * `readPlanDraft` does: an undo landing mid-gesture must change the answer.
 */
export function previewMoveCommand(command: MoveCommand): MoveOutcome {
  const draft = usePlanDraftStore.getState().draft;
  return draft === null ? { kind: 'refused', reason: 'unknownTarget' } : moveOutcome(draft, command);
}

/**
 * Run one move — the ONE write behind every drag mechanic, and the same one the keyboard path and
 * the ⋯ menu dispatch.
 *
 * View ADR §12: the menu path "dispatches the same command the drop does — it is not a reduced
 * fallback". This function is what makes that structural rather than a promise; there is no second
 * entry point for a pointer to take. That claim was FALSE between 10 August and 7 September 2026 —
 * the library rail's press called `addExercise` directly, skipping `libraryDropRefusal` and every
 * worded outcome with it — and the rail now dispatches `insertFromLibrary` like everything else.
 *
 * A refusal and a no-op both return BEFORE `runOperation`, so neither records a patch, pushes an
 * undo entry or marks the plan unsaved. That is what makes a cancelled or refused drag cost the
 * trainer nothing — see `moveCommands`' header.
 *
 * The outcome is computed against the PRE-move tree and returned to the caller to word. It is read
 * once and used twice — the gate and the announcement are the same answer, so they cannot disagree.
 */
export function runMoveCommand(command: MoveCommand): MoveOutcome {
  const outcome = previewMoveCommand(command);
  if (outcome.kind === 'refused' || outcome.kind === 'unchanged') return outcome;
  let created: string | null = null;
  runOperation((draft) => {
    created = applyMove(draft, command);
  });
  if (outcome.kind !== 'inserted') return outcome;
  // The one thing the pre-move answer cannot carry: a token that did not exist when it was
  // computed. `addExercise`'s rule — a create names what it created — applied to the command
  // vocabulary, so the library drawer can send focus to the card its press just made without a
  // second entry point to guess at.
  //
  // **An insert with no token is not an insert.** `applyMove` answers `null` when the operation
  // found nothing to write to, and the announcement was built from the PRE-move outcome regardless
  // — so the trainer could be told a movement had arrived over a tree nothing was added to. It is
  // unreachable today, because the gate above already asked the day and the cap; the point is that
  // it cannot be reached by a future gate that stops agreeing with the operation, which is why
  // `exerciseSlotToken` is `string` and this branch exists rather than a `?? null`.
  if (created === null) return { kind: 'refused', reason: 'unknownTarget' };
  return { ...outcome, exerciseSlotToken: created };
}

/** Undo the last recorded edit. A no-op with an empty stack, so a caller never has to check first. */
export function undoPlanDraftEdit(): void {
  usePlanDraftStore.setState((state) => {
    const entry = state.past[state.past.length - 1];
    if (entry === undefined || state.draft === null) return state;
    return {
      draft: applyPatches(state.draft, entry[1]),
      past: state.past.slice(0, -1),
      future: [entry, ...state.future],
      revision: state.revision + 1,
      ...(entry[2] === undefined ? {} : { aiApplied: withApplyStanding(state.aiApplied, entry[2], false) }),
    };
  });
}

/** Reapply the last undone edit. A no-op with an empty redo stack. */
export function redoPlanDraftEdit(): void {
  usePlanDraftStore.setState((state) => {
    const entry = state.future[0];
    if (entry === undefined || state.draft === null) return state;
    return {
      draft: applyPatches(state.draft, entry[0]),
      // Capped on the way back too, so a long redo run cannot grow the stack past the limit an
      // equally long edit run respects.
      past: [...state.past, entry].slice(-UNDO_LIMIT),
      future: state.future.slice(1),
      revision: state.revision + 1,
      ...(entry[2] === undefined ? {} : { aiApplied: withApplyStanding(state.aiApplied, entry[2], true) }),
    };
  });
}

/**
 * Read the tree outside React — the save path needs it, and it must read the CURRENT tree at the
 * moment the trainer pressed the button rather than whatever a render closed over.
 */
export function readPlanDraft(): PlanDraft | null {
  return usePlanDraftStore.getState().draft;
}

/**
 * Are there unsaved edits RIGHT NOW — the same answer `useIsPlanDraftDirty` renders, read outside
 * React.
 *
 * Its one caller is a close handler that has just rolled an edit of its own back: the render's
 * closure holds the flag from before the rollback, so asking the hook would put an "unsaved work"
 * guard in front of a trainer whose only unsaved work was the insert that was just undone.
 */
export function readIsPlanDraftDirty(): boolean {
  return usePlanDraftStore.getState().past.length > 0;
}

/**
 * Who the unsaved plan is being built for, read outside React — the assign dialog snapshots it,
 * with the tree, at the moment it opens.
 */
export function readPlanRecipient(): PlanRecipient | null {
  return usePlanDraftStore.getState().recipient;
}

/** The pristine load, for the 409 diff. Outside React for the same reason as `readPlanDraft`. */
export function readPlanDraftBaseline(): PlanDraft | null {
  return usePlanDraftStore.getState().baseline;
}

/**
 * The revision as it stands RIGHT NOW — see `PlanDraftState.revision` for what the number means.
 *
 * Outside React for the same reason as the two above, and for one specific caller: a surface that
 * has just dispatched an edit and wants to show a note ABOUT that edit needs to know which document
 * the note describes, so it can stop showing it the moment the trainer changes the document again.
 * A render's closure holds the revision from BEFORE the dispatch, which is one behind.
 */
export function readPlanDraftRevision(): number {
  return usePlanDraftStore.getState().revision;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Selectors, exported ONLY as narrow hooks.
//
// The sessionStore pattern (`entities/session/lib/sessionStore.ts`): "the narrow-selector rule is
// easier to follow than to remember". A hook named for the one fact it returns cannot accidentally
// subscribe a screen to the whole document the way an inline selector can — and a component
// subscribed to the whole document re-renders on every keystroke anywhere in the plan.
//
// Each selector is a module-level function so its identity is stable across renders. The React
// Compiler does not help here and must not be relied on to: it memoises WITHIN a component, and a
// zustand selector's identity matters to the store's subscription, which outlives one render.
// ─────────────────────────────────────────────────────────────────────────────────────────────

const selectDraft = (state: PlanDraftState): PlanDraft | null => state.draft;
const selectDirty = (state: PlanDraftState): boolean => state.past.length > 0;
const selectCanUndo = (state: PlanDraftState): boolean => state.past.length > 0;
const selectCanRedo = (state: PlanDraftState): boolean => state.future.length > 0;
const selectRevision = (state: PlanDraftState): number => state.revision;

/**
 * The whole tree.
 *
 * The one WIDE selector, and it has exactly one legitimate caller:
 * `pages/plan-builder/lib/usePlanDocument.ts`, the seam every builder surface reads through. The
 * week view renders from the tree's shape, so something has to hand it the tree; what the narrow
 * rule prevents is a COMPONENT doing it. Anything below the seam takes a projection.
 */
export function usePlanDraftTree(): PlanDraft | null {
  return usePlanDraftStore(selectDraft);
}

/**
 * Are there unsaved edits? The emptiness of the patch stack — see the header for what that does and
 * does not mean.
 */
export function useIsPlanDraftDirty(): boolean {
  return usePlanDraftStore(selectDirty);
}

export function useCanUndoPlanDraft(): boolean {
  return usePlanDraftStore(selectCanUndo);
}

export function useCanRedoPlanDraft(): boolean {
  return usePlanDraftStore(selectCanRedo);
}

/**
 * A number that changes every time the tree does — see `PlanDraftState.revision`. Narrow by
 * construction: a subscriber re-renders on each change and can read nothing about WHAT changed,
 * which is the whole of what its one caller needs.
 */
export function usePlanDraftRevision(): number {
  return usePlanDraftStore(selectRevision);
}

/**
 * What this session's applies of `proposal` stand at, by change id (see `AiApplyStanding`). The
 * same map until an apply, an undo or a redo of this proposal moves it.
 */
export function useAiApplyStanding(proposal: AiProposal): AiApplyStanding {
  const key = proposalKey(proposal);
  return usePlanDraftStore((state) => state.aiApplied.get(key) ?? NO_AI_APPLY_STANDING);
}

/**
 * Whether an exercise or a day still holds what Buddy wrote into it — what tells its values apart
 * from the trainer's own (ai-plan-builder R15). False from the trainer's first edit of it.
 */
export function useIsAiWrittenValue(exerciseSlotToken: string, setIndex: number, valueKey: AiWrittenValueKey): boolean {
  return usePlanDraftStore(
    (state) => state.draft !== null && isAiWrittenValue(state.aiWritten, state.draft, exerciseSlotToken, setIndex, valueKey),
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The DAY DOCK's three reads (PR-B2C).
//
// The dock renders one day out of a tree that may hold a hundred, and it must not re-render when
// another phase's day changes. These are the narrowest questions that answer it, and each one
// returns a value `Object.is` can compare:
//
// · a NODE, whose identity Immer's structural sharing preserves across every edit that did not
//   touch it — so subscribing to a day is subscribing to that day;
// · a STRING, for a neighbour's address;
// · a BOOLEAN, for the one plan-scoped fact the day cannot see on its own.
//
// **None of them may return a fresh object.** Zustand v5 reads the selector inside
// `useSyncExternalStore`'s `getSnapshot`, which React calls more than once per commit — a selector
// minting a new object every call reports a changed snapshot forever and re-renders in a loop.
// That is why the removal guard below is a boolean rather than the refusal value itself, and why
// there is no `useDayContext()` returning the three together.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** Walk to the phase a day sits in, and the day itself. Internal — neither leaves this module. */
function locateDay(
  draft: PlanDraft | null,
  daySlotToken: string | null,
): { readonly phaseIndex: number; readonly dayIndex: number } | null {
  if (draft === null || daySlotToken === null) return null;
  for (const [phaseIndex, phase] of draft.phases.entries()) {
    const dayIndex = phase.days.findIndex((day) => day.slotToken === daySlotToken);
    if (dayIndex !== -1) return { phaseIndex, dayIndex };
  }
  return null;
}

/**
 * The one day the dock is editing, or `null` when the token names nothing — a stale `?day=` in a
 * bookmark, or a day another tab deleted.
 *
 * `null` is a normal answer rather than an error: the dock says the day is gone and closes.
 */
export function useDayDraft(daySlotToken: string | null): DayDraft | null {
  return usePlanDraftStore((state) => {
    const site = locateDay(state.draft, daySlotToken);
    if (site === null) return null;
    return state.draft?.phases[site.phaseIndex]?.days[site.dayIndex] ?? null;
  });
}

/**
 * The token of the day before or after this one WITHIN ITS PHASE — what the header's ‹ › move to.
 *
 * Phase-scoped on purpose: a phase is the unit a slot lives in (an exercise's progression chain
 * does not cross a phase boundary), so stepping out of one would land the trainer on a day whose
 * every parameter belongs to a different block of the plan without saying so.
 */
export function useAdjacentDayToken(
  daySlotToken: string | null,
  direction: 'previous' | 'next',
): string | null {
  return usePlanDraftStore((state) => {
    const site = locateDay(state.draft, daySlotToken);
    if (site === null) return null;
    const days = state.draft?.phases[site.phaseIndex]?.days ?? [];
    const target = site.dayIndex + (direction === 'next' ? 1 : -1);
    return days[target]?.slotToken ?? null;
  });
}

/**
 * The days of the phase this day sits in, or `null` when no phase holds it.
 *
 * The array itself, not a copy: Immer keeps its identity across every edit that did not touch the
 * phase's days, so a subscriber re-renders when a day of the phase changes and not otherwise. Its
 * one reader is the Buddy page's session panel, which names the sessions it can step and move to.
 */
export function useDayPhaseDays(daySlotToken: string | null): readonly DayDraft[] | null {
  return usePlanDraftStore((state) => {
    const site = locateDay(state.draft, daySlotToken);
    return site === null ? null : (state.draft?.phases[site.phaseIndex]?.days ?? null);
  });
}

/**
 * The FIRST absolute cycle of the phase this day sits in, and how many cycles that phase runs.
 *
 * Two hooks rather than one returning the window, for the reason the progression section's own
 * reads state at length below: `findDayPhaseWindow` mints a fresh object on every call, and a
 * selector doing that inside `useSyncExternalStore`'s `getSnapshot` reports a changed snapshot
 * forever and re-renders in a loop. Numbers compare with `Object.is`; the caller reassembles them.
 *
 * They exist for the calendar's day editor, which renders one CYCLE of a day rather than its
 * authored base. The projection's origin is each exercise's own activation clamped into this frame
 * (`exerciseCycleWindow`), so the phase's span is what a surface has to be able to ask for.
 */
export function useDayPhaseStartCycle(daySlotToken: string | null): number | null {
  return usePlanDraftStore((state) =>
    state.draft === null || daySlotToken === null
      ? null
      : (findDayPhaseWindow(state.draft.phases, daySlotToken)?.startCycle ?? null),
  );
}

/** How many cycles that phase runs. See `useDayPhaseStartCycle`. */
export function useDayPhaseCycleCount(daySlotToken: string | null): number | null {
  return usePlanDraftStore((state) =>
    state.draft === null || daySlotToken === null
      ? null
      : (findDayPhaseWindow(state.draft.phases, daySlotToken)?.cycleCount ?? null),
  );
}

/**
 * Why this exercise may not be removed, or `null` when it may.
 *
 * The entity's own predicate, subscribed to rather than re-derived: `exerciseRemovalRefusal` is
 * PHASE-scoped (emptying the last stocked day of a phase makes the whole plan unsavable, in a phase
 * the trainer may not be looking at), and a feature computing that from a day node would be a second
 * copy of the rule that owns it.
 *
 * The refusal is a string union, so a snapshot compares by `Object.is` and this is as narrow as the
 * node hooks above. The WORDS for it belong to whichever surface shows the disabled control.
 */
export function useExerciseRemovalRefusal(
  exerciseSlotToken: string,
): operations.ExerciseRemovalRefusal | null {
  return usePlanDraftStore((state) =>
    state.draft === null ? null : operations.exerciseRemovalRefusal(state.draft, exerciseSlotToken),
  );
}

/** Whether "link with the next" is offered here. The entity's predicate, subscribed to. */
export function useCanGroupWithNext(exerciseSlotToken: string): boolean {
  return usePlanDraftStore((state) =>
    state.draft !== null && operations.canGroupWithNext(state.draft, exerciseSlotToken),
  );
}

/**
 * May this day be picked up at all — by pointer, by keyboard or from the menu?
 *
 * A boolean, so it is as narrow as the node hooks above. It answers the question a GRIP asks before
 * it renders.
 */
export function useCanLiftDay(daySlotToken: string): boolean {
  return usePlanDraftStore(
    (state) => state.draft !== null && canLiftDay(state.draft, daySlotToken),
  );
}

/** The same question for one exercise. False for a standalone warm-up, which holds no position. */
export function useCanLiftExercise(exerciseSlotToken: string): boolean {
  return usePlanDraftStore(
    (state) => state.draft !== null && canLiftExercise(state.draft, exerciseSlotToken),
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The PROGRESSION section's reads (PR-D).
//
// The rule bar and the matrix mount inside one exercise's block and must not re-render when another
// exercise changes. Same three shapes as the dock's reads above, for the same reason — a node, and
// numbers — and the same absolute prohibition on returning a fresh object.
//
// **`useExerciseCycleWindow` is deliberately absent, and its absence is the rule in action.**
// `exerciseCycleWindow` returns `{ startCycle, cycleCount }`, a fresh object on every call, which
// inside `getSnapshot` reports a changed snapshot forever and re-renders in a loop. The window is
// therefore read as its two numbers, and the caller reassembles them — which costs a line at the
// call site and cannot deadlock a render.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The one exercise a progression section is attached to, or `null` when the token names nothing.
 *
 * A NODE, so Immer's structural sharing does the narrowing: retyping a weight in another exercise
 * leaves this one's identity untouched and this subscriber does not re-render. The walk is
 * plan-wide, matching `useExerciseRemovalRefusal` and `useCanGroupWithNext`, which already subscribe
 * per exercise row the same way.
 */
export function useExerciseDraft(exerciseSlotToken: string): ExerciseDraft | null {
  return usePlanDraftStore(
    (state) => operations.findExerciseNode(state.draft, exerciseSlotToken) ?? null,
  );
}

/**
 * The exercise's FIRST active cycle — its activation cycle, or its phase's start when it has none.
 * The matrix's leftmost column, and the origin every projection is measured from. `null` when the
 * token names nothing.
 */
export function useExerciseBaseCycle(exerciseSlotToken: string): number | null {
  return usePlanDraftStore(
    (state) =>
      state.draft === null
        ? null
        : (findExerciseCycleWindow(state.draft.phases, exerciseSlotToken)?.startCycle ?? null),
  );
}

/** How many cycles the exercise's window spans — how many columns the matrix draws. */
export function useExerciseCycleCount(exerciseSlotToken: string): number | null {
  return usePlanDraftStore(
    (state) =>
      state.draft === null
        ? null
        : (findExerciseCycleWindow(state.draft.phases, exerciseSlotToken)?.cycleCount ?? null),
  );
}

/**
 * The exercise's progression rules, EMPTY when it carries none — and when the token names nothing,
 * which read the same here on purpose: a statement about a row that is gone says the same as a
 * statement about a row with no rule. The empty answer is the shared `NO_PROGRESSION_RULES` constant
 * rather than a fresh `{}`: a selector minting a new object on every call reports a changed snapshot
 * forever and re-renders in a loop.
 *
 * **The map is built once at load** (see `ExerciseDraft.progressionRules`), so this returns a stable
 * reference and a subscriber re-renders only when the exercise is replaced wholesale — an undo, a
 * redo, a conflict adoption, or a rule actually being authored. `useExerciseDraft` would answer the same question by subscribing to the
 * whole node, which re-renders on every keystroke in that exercise's set table; the panel this feeds
 * is drawn once per exercise, six times over a day, and none of them has anything to do with a
 * weight being typed.
 */
export function useExerciseProgressionRules(exerciseSlotToken: string): ProgressionRules {
  return usePlanDraftStore(
    (state) =>
      operations.findExerciseNode(state.draft, exerciseSlotToken)?.progressionRules ??
      NO_PROGRESSION_RULES,
  );
}

/**
 * The entries of the same stored document this build has no reading for, EMPTY for every document
 * the current contract can produce.
 *
 * A hook of its own rather than a pair returned beside the readable map, for the reason every narrow
 * hook here exists: both halves are stable references on the exercise node, and a selector minting
 * an object to hold them would report a changed snapshot on every call and re-render forever.
 */
export function useExerciseUnreadableProgressionRules(
  exerciseSlotToken: string,
): Readonly<Record<string, unknown>> {
  return usePlanDraftStore(
    (state) =>
      operations.findExerciseNode(state.draft, exerciseSlotToken)?.unreadableProgressionRules ??
      NO_STORED_RULES.unreadable,
  );
}

/**
 * The name of the phase this exercise's slot lives in, or `null` when the token names nothing.
 *
 * A STRING, so a subscriber re-renders only when the phase it sits in is renamed — narrower than
 * the node hooks above and deliberately so: its one consumer is a context line under a grid, and
 * that line must not re-render on every keystroke in a set table three phases away.
 */
export function useExercisePhaseName(exerciseSlotToken: string): string | null {
  return usePlanDraftStore(
    (state) =>
      state.draft?.phases.find((phase) =>
        phase.days.some((day) =>
          day.exercises.some((exercise) => exercise.slotToken === exerciseSlotToken),
        ),
      )?.name ?? null,
  );
}

/**
 * How many leading cycles the client has already consumed — zero outside a live fork, and zero on a
 * fork the client has not started. The matrix locks exactly that many leading columns.
 */
export function useConsumedCycleCount(): number {
  return usePlanDraftStore((state) =>
    state.draft === null ? 0 : consumedCycleCount(state.draft.live),
  );
}

/**
 * The cycles the SERVER says an edit can no longer reach on the day holding this exercise,
 * `DayDraft.liveClosedCycles`, stamped by `GET /plan-template/{id}/full` from the same frozen record
 * the save judges by, or `null` where the surface that answered did not say.
 *
 * Per DAY because reachability is: within one plan-wide consumed prefix a day whose session is still
 * ahead of the client is open while a day already lived is closed, and the progression sheet's cells
 * and its „TU JEST KLIENT" column follow that rather than the plan-wide boundary. Returns the node's
 * own array, so Immer's structural sharing keeps it stable across edits that did not touch the day.
 */
export function useExerciseDayClosedCycles(exerciseSlotToken: string): readonly number[] | null {
  return usePlanDraftStore((state) => {
    for (const phase of state.draft?.phases ?? []) {
      for (const day of phase.days) {
        if (day.exercises.some((exercise) => exercise.slotToken === exerciseSlotToken)) {
          return day.liveClosedCycles;
        }
      }
    }
    return null;
  });
}

/**
 * Is this document a client's fork rather than a library template? The e1RM context line's gate:
 * a library template has no client, so there is no max to read and no request to make.
 */
export function useIsForkPlan(): boolean {
  return usePlanDraftStore((state) => state.draft !== null && isLivePlan(state.draft.live));
}

/**
 * Is this document a LIBRARY entry — a programme of the trainer's own, which nobody is training?
 *
 * Not `!useIsForkPlan()`. That answers `true` for a document that has not loaded at all, and the one
 * caller of this is the plan menu's deletion: a surface that reads the absence of a fork as a
 * library entry would offer to delete a plan it has not read yet. Three states, and this names one.
 */
export function useIsLibraryEntryPlan(): boolean {
  return usePlanDraftStore((state) => state.draft !== null && !isLivePlan(state.draft.live));
}

/**
 * The template id being edited, or `null` before the first load.
 *
 * Read from the DOCUMENT rather than from the route, so a feature needing it does not have to import
 * the router — and so it answers `null` for a brand-new draft that has no id yet, which a route
 * parameter cannot express.
 */
export function usePlanDraftId(): number | null {
  return usePlanDraftStore((state) => state.draft?.id ?? null);
}

/**
 * The Buddy conversation an UNSAVED plan came out of (`origin: aiDraft`), or `null`: the builder's
 * assistant goes on in that conversation rather than starting another (ai-plan-builder R13).
 */
export function usePlanAiConversationId(): number | null {
  return usePlanDraftStore((state) =>
    state.draft !== null && state.draft.id === null && state.draft.origin.kind === 'aiDraft' ? state.draft.origin.conversationId : null,
  );
}

/**
 * The name of the first phase that makes this tree unsavable, or `null` when it is savable.
 *
 * A STRING, so a snapshot compares by `Object.is` and this is as narrow as the node hooks above —
 * and it is the one shape the answer can take without minting a fresh object inside `getSnapshot`,
 * which would re-render in a loop.
 *
 * Its one consumer is the builder's Save button, which has to explain a refusal BEFORE the press: a
 * plan the create modal seeded has an empty phase from the moment it opens, and the backend answers
 * that with a 400 the trainer never sees. The rule itself is `firstEmptyPhaseName`'s — subscribed to
 * here rather than re-derived, so the gate and the serialiser cannot disagree about what "empty"
 * means.
 */
export function useSaveBlockingPhaseName(): string | null {
  return usePlanDraftStore(blockingPhaseName);
}

/**
 * The same answer, asked only of a plan being built FOR a client — `null` for every other document,
 * which the assign gate judges by its stored row instead. Kept apart from the save's subscription so
 * a stored plan, the common case, never walks its tree a second time on every edit.
 */
export function useUnsavedAssignBlockingPhaseName(): string | null {
  return usePlanDraftStore((state) => (state.recipient === null ? null : blockingPhaseName(state)));
}

function blockingPhaseName(state: PlanDraftState): string | null {
  return state.draft === null ? null : firstEmptyPhaseName(state.draft);
}

/**
 * Is the document a plan that has never been stored — a seed from the create modal, still living
 * only in this tab?
 *
 * A BOOLEAN, so it is as narrow as the node hooks above, and it is deliberately not derivable from
 * `usePlanDraftId()`: that answers `null` both for "a brand-new plan" and for "no document at all",
 * and the two need opposite treatment. A new plan is unsaved work worth warning about; an empty
 * store is nothing.
 *
 * Two consumers, and both are about not losing the trainer's work: the Save button, which must be
 * live on a plan whose undo stack is empty because nothing has been edited YET, and the navigation
 * blocker, which must ask before the tab closes over a plan that exists nowhere else.
 */
export function useIsUnsavedNewPlan(): boolean {
  // An ASSIGNED unsaved plan is not unsaved work: it lives on as the client's plan, and the builder
  // behind the confirmation must not ask before the trainer leaves it.
  return usePlanDraftStore(
    (state) => state.draft !== null && state.draft.id === null && !state.assigned,
  );
}

/**
 * Who the unsaved plan is being built for, or `null`. The stored object, so `Object.is` holds it
 * across every render that did not re-seed.
 *
 * Two consumers: the builder, which offers no Zapisz while a client is set (the assignment is the
 * save), and the assign gate, which judges the tree itself rather than a stored row.
 */
export function usePlanRecipient(): PlanRecipient | null {
  return usePlanDraftStore((state) => state.recipient);
}

/**
 * Which canvas the plan is drawn on — and therefore the unit noun every count is worded with.
 *
 * A string, so it is as narrow as the node hooks above. Execution ADR §7: the noun is INTERPOLATED
 * from the mode rather than baked into a key, which is why mobile's `…Seq` locale twins are copied
 * with the files and never referenced.
 */
export function usePlanSchedulingMode(): PlanSchedulingMode {
  return usePlanDraftStore((state) => state.draft?.mode ?? 'date');
}
